import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { OrderService } from '../../../services/order.service';
import { OrderResponse, OrderStatusResponse } from '../../../models/orders/order.model';
import { ScheduleOrderResponse, ScheduleResponse } from '../../../models/schedule.model';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { SchedulesService } from '../../../services/schedules.service';
import { ToastrService } from 'ngx-toastr';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { ScheduleOrderStatusVisualComponent } from '../schedule-orders/schedule-order-status-visual/schedule-order-status-visual.component';
import { DisplayOrderInformationComponent } from '../../orders/display-order-information/display-order-information.component';
import { GetOrCreatePlaceRequest, PlaceResponse } from '../../../models/orders/place.model';
import { DisplayPlaceFieldComponent } from '../../places/display-place-field/display-place-field.component';
import { SearchPlaceDialogComponent } from '../../places/search-place-dialog/search-place-dialog.component';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { forkJoin, finalize } from 'rxjs';
import { PlaceService } from '../../../services/place.service';

@Component({
  selector: 'app-schedule-pickup',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatListModule,
    MatIconModule,
    MatCardModule,
    ScheduleOrderStatusVisualComponent,
    DisplayOrderInformationComponent,
    DisplayPlaceFieldComponent,
    SearchPlaceDialogComponent,
    MatSelectModule,
    MatInputModule
  ],
  templateUrl: './schedule-pickup.component.html',
  styleUrl: './schedule-pickup.component.scss'
})
export class SchedulePickupComponent implements OnInit {
  orderReference!: string;
  order!: OrderResponse | null;
  scheduleOrder!: ScheduleOrderResponse;
  scheduleOrderId!: string;
  statuses: OrderStatusResponse[] = []; 
  validStatuses: string[] = []; 
  validStatusObjects: OrderStatusResponse[] = []; 
  schedule!: ScheduleResponse;
  statusId: string | null = null; 
  status!: OrderStatusResponse | null; 
  place!: PlaceResponse | null; 
  placeId: string | null = null; 
  scheduleStatus: string | null = null;
  nextScheduleOrderStatus: string | null; 
  isLoading: boolean = false;
  scheduleType: string|null;

  constructor(
    private orderService: OrderService,
    private scheduleService: SchedulesService,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private placeService: PlaceService,
    private dialogRef: MatDialogRef<SchedulePickupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      scheduleType: string,
      orderReference: string,
      scheduleOrderId: string,
      placeId: string | null,
      scheduleOrder: ScheduleOrderResponse,
      schedule: ScheduleResponse,
    }
  ) {
    this.orderReference = data.orderReference;
    this.scheduleOrderId = data.scheduleOrderId;
    this.scheduleOrder = data.scheduleOrder;
    this.schedule = data.schedule;

    this.scheduleType = data.scheduleType;
    this.placeId = data.placeId ?? null;

    // Calculate next scheduleOrder status
    this.nextScheduleOrderStatus = this.calculateNextScheduleOrderStatus(this.scheduleOrder);
    this.scheduleStatus = this.nextScheduleOrderStatus;
  }

  ngOnInit(): void {
    this.getOrder();
  }

  calculateNextScheduleOrderStatus(scheduleOrder: ScheduleOrderResponse): string | null {
    if (scheduleOrder?.status) {
      switch (scheduleOrder.status) {
        case 'Scheduled': return 'InProgress';
        case 'InProgress': return 'Completed';
        default: return null;
      }
    }
    return null;
  }

  // This is messy helper function that's safe to run, fails relatively often
  // but can autofill data to make life easier for the user if it works.
  calculateInferredPlaceId(): void {
    if( this.scheduleType === 'Dropoff' )
    {
      if(this.scheduleOrder.status === 'InProgress')
      {
        // If the order is in progress, we can use the locationId from the order's dropoff location
        const data = {
          name: "Dropoff Address",
          objectId: undefined,
          type: "Custom"
        };
        this.createPlace(data);
        return;
      }
      if(this.scheduleOrder.status === 'Scheduled' && this.schedule && this.schedule.vehicle)
      {
        // If the order is scheduled, we can use the vehicleId from the schedule
        const data = {
          name: this.schedule.vehicle.name,
          objectId: this.schedule.vehicle.id,
          type: "Vehicle"
        };
        this.createPlace(data);
        return;
      }
    }
    if( this.scheduleType === 'Pickup' )
    {
      if(this.scheduleOrder.status === 'InProgress')
        {
          // If the order is in progress, we cannot infer the placeId, but if it's passed in, we can assign it here.
          return;
        }
        if(this.scheduleOrder.status === 'Scheduled' && this.schedule && this.schedule.vehicle)
        {
          // If the order is scheduled, we can use the vehicleId from the schedule - orders go in the vehicle first
          const data = {
            name: this.schedule.vehicle.name,
            objectId: this.schedule.vehicle.id,
            type: "Vehicle"
          };
          this.createPlace(data);
          return;
        }
    }
    return;
  }

  createPlace(data: GetOrCreatePlaceRequest):void {
    this.placeService.getOrCreatePlace(data).subscribe({
      next: (place) => {
        if (place) {
          this.placeId = place.id;
          this.place = place;
        } else {
          console.error('Place not found or created:', place);
          this.toastr.error('Failed to create or retrieve the place.', 'Error');
        }
      },
      error: (error) => {
        console.error('Error creating or retrieving place:', error);
        this.toastr.error('Failed to create or retrieve the place.', 'Error');
      }
    });
  }

  clearPlace(): void {
    this.placeId = null;
    this.place = null;
  }

  clearStatus(): void {
    this.statusId = null;
    this.status = null;
  }

  selectStatus(statusId: string): void {
    this.statusId = statusId;
    const foundStatus = this.statuses.find(s => s.id === statusId);
    if (foundStatus) {
      this.status = foundStatus;
    } else {
      console.warn(`Selected statusId ${statusId} not found in available statuses.`);
      this.toastr.warning('Selected status was not available.', 'Warning');
      this.clearStatus(); // Reset if the ID doesn't match any status object
    }
  }

  openPlaceDialog(): void {
    const dialogRef = this.dialog.open(SearchPlaceDialogComponent, {
      width: '600px',
    });
    dialogRef.afterClosed().subscribe({
      next: (result: PlaceResponse | undefined) => {
        if (result) {
          this.placeId = result.id;
          this.place = result;
        }
      },
      error: (error) => {
        console.error('Error *after* closing place dialog', error);
        this.toastr.error('Could not process place selection.', 'Error');
      }
    });
  }

  getOrder(): void {
    const orderId = this.scheduleOrder?.order?.id ?? null;
    if (!orderId) {
      console.error('Order ID not found on scheduleOrder');
      this.toastr.error('Could not determine the Order ID to fetch.', 'Configuration Error');
      return;
    }

    this.isLoading = true;

    forkJoin({
      // Fetch all possible statuses
      statusesResult: this.orderService.getOrderStatuses(),
      // Fetch the specific order details (including its current status and valid transitions)
      orderResult: this.orderService.getOrder(orderId)
    })
    .pipe(
      finalize(() => this.isLoading = false)
    )
    .subscribe({
      next: ({ statusesResult, orderResult }) => {
        // --- Both requests successful ---

        this.statuses = statusesResult.items || [];
        this.order = orderResult;

        // Determine valid transitions from the fetched order
        this.validStatuses = this.order?.status?.validTransitions || [];

        // Decide on the initial statusId (only if not already set, and only if exactly one option)
        if (this.validStatuses.length === 1 && !this.statusId) {
           this.statusId = this.validStatuses[0];
        }

        this.getValidStatuses(); 
        this.calculateInferredPlaceId(); // Attempt to infer the placeId based on the order status and schedule type

        if (this.statusId) {
          const foundStatus = this.statuses.find(s => s.id === this.statusId);
          if (foundStatus) {
            this.status = foundStatus;
          } else {
            console.warn(`Initial statusId ${this.statusId} not found in available statuses. Resetting.`);
            this.toastr.warning('Selected status was not available.', 'Warning');
            this.clearStatus(); // Reset if the ID doesn't match any status object
          }
        } else {
           this.status = null;
        }

      },
      error: (error) => {
        console.error('Error fetching order details or statuses:', error);
        this.toastr.error('Failed to load required order information.', 'Error');
        this.order = null;
        this.statuses = [];
        this.validStatuses = [];
        this.validStatusObjects = [];
        this.status = null;
        this.statusId = null;
      }
    });
  }

  getValidStatuses(): void {
    if (this.validStatuses?.length > 0 && this.statuses?.length > 0) {
      this.validStatusObjects = this.statuses.filter(status =>
        this.validStatuses.includes(status.id)
      );
    } else {
      this.validStatusObjects = []; 
    }
  }

  onSubmit(): void {
    if (this.statusId === null || this.statusId === undefined) {
        this.toastr.error('An order status must be selected.', 'Missing Information');
        console.error('Status is required');
        return;
    }
    if (this.placeId === null || this.placeId === undefined) {
        this.toastr.error('A location must be selected.', 'Missing Information');
        console.error('Place is required');
        return;
    }
     if (this.scheduleStatus === null || this.scheduleStatus === undefined) {
        this.toastr.error('Cannot determine the next schedule status.', 'Configuration Error');
        console.error('Next schedule status is required');
        return;
    }

    this.isLoading = true;

    this.scheduleService.processScheduleOrder(
      this.schedule.id,
      this.order!.id,
      this.statusId,
      this.placeId,
      this.scheduleStatus 
    ).pipe(
      finalize(() => this.isLoading = false) // Stop loading
    ).subscribe({
      next: (response) => {
        this.toastr.success('Order processed successfully', 'Success');
        this.dialogRef.close(response);
      },
      error: (error) => {
        console.error('Failed to process order:', error);
        const errorMsg = error?.error?.message || 'Failed to process order due to a server error.';
        this.toastr.error(errorMsg, 'Error');
      }
    });
  }
}